import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaChevronDown,
  FaBars,
  FaTimes,
  FaUserTie,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaBook,
  FaBriefcase,
  FaPhoneAlt,
  FaRocket,
  FaLayerGroup,
  FaMobileAlt,
} from "react-icons/fa";
import { COLORS } from "../constants/colors.js";
import ERPModal from "../Components/ERPModal.js";

const MODULES = [
  { id: "admin-panel", label: "Admin Panel", icon: <FaUserTie /> },
  { id: "teacher-panel", label: "Teacher Panel", icon: <FaChalkboardTeacher /> },
  { id: "student-panel", label: "Student Panel", icon: <FaUserGraduate /> },
  { id: "library-panel", label: "Library Panel", icon: <FaBook /> },
   { id: "erp-mobile-app", label: "Mobile App", icon: <FaMobileAlt /> },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dropdownRef = useRef(null);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    setIsScrolled(scrollTop > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setModulesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const toggleModules = () => setModulesOpen((prev) => !prev);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled ? "backdrop-blur-md shadow-md" : ""
      }`}
      style={{
        background: isScrolled ? "rgba(255,255,255,0.8)" : COLORS.primaryLight,
      }}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-6  py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2" aria-label="Home">
          <img
            src="/assets/logo_header.png"
            alt="Logo"
            className="w-[300px] h-[50px] object-cover"/>
          
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-8 font-medium relative">
          {/* Our Modules */}
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={() => setModulesOpen(true)}
            onMouseLeave={() => setModulesOpen(false)}
          >
            <button
              onClick={toggleModules}
              className="flex items-center text-gray-700 hover:text-sky-600 space-x-2"
            >
              <FaLayerGroup />
              <span>Our Modules</span>
              <FaChevronDown
                className={`text-sm transition-transform ${
                  modulesOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown */}
            <div
              className={`absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-lg py-2 z-50 transform transition-all duration-300 origin-top ${
                modulesOpen
                  ? "opacity-100 scale-y-100 visible"
                  : "opacity-0 scale-y-75 invisible"
              }`}
            >
              {MODULES.map(({ id, label, icon }) => (
                <Link
                  key={id}
                  to={`/${id}`}
                  className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-sky-50 transition"
                >
                  <span className="text-sky-600">{icon}</span>
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Career Opportunity */}
          <Link
            to="/career"
            className="flex items-center gap-2 text-gray-700 hover:text-sky-600"
          >
            <FaBriefcase />
            Career Opportunity
          </Link>

          {/* Contact Us */}
          <Link
            to="/contact"
            className="flex items-center gap-2 text-gray-700 hover:text-sky-600"
          >
            <FaPhoneAlt />
            Contact Us
          </Link>

          {/* Call Now (Stylish Number) */}
          <a
            href="tel:+918295000455"
            className="flex items-center gap-2 text-sky-600 font-bold hover:text-blue-700 transition"
          >
            | +91 8295000455
          </a>
        </div>

        {/* Desktop Buttons */}
        <div className="space-x-3 hidden md:flex">
          <button
            className="bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-full font-semibold transition-all flex items-center gap-2 hover:shadow-lg"
            style={{ backgroundColor: COLORS.secondary }}
            onClick={() => setShowModal(true)}
          >
            <FaRocket className="text-white hover:scale-110 transition-transform" />
            Get Free Demo →
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMenu}
          className="md:hidden text-gray-700 focus:outline-none"
        >
          {menuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
        </button>
      </div>

      {/* Mobile Dropdown */}
      <div
        className={`md:hidden bg-white shadow-lg absolute top-full left-0 w-full transition-all duration-500 ease-in-out overflow-hidden ${
          menuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="flex flex-col items-start p-4 space-y-4">
          {/* Our Modules */}
          <div className="w-full">
            <button
              onClick={toggleModules}
              className="flex items-center justify-between w-full text-gray-700 hover:text-sky-600 font-medium"
            >
              <span className="flex items-center gap-2">
                <FaLayerGroup />
                Our Modules
              </span>
              <FaChevronDown
                className={`text-sm transition-transform ${
                  modulesOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown for Modules */}
            <div
              className={`transition-all duration-300 overflow-hidden ${
                modulesOpen ? "max-h-64 opacity-100 mt-2" : "max-h-0 opacity-0"
              }`}
            >
              {MODULES.map(({ id, label, icon }) => (
                <Link
                  key={id}
                  to={`/${id}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-sky-50 transition rounded-md"
                >
                  <span className="text-sky-600">{icon}</span>
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Career Opportunity */}
          <Link
            to="/career"
            className="flex items-center gap-2 text-gray-700 hover:text-sky-600"
            onClick={() => setMenuOpen(false)}
          >
            <FaBriefcase />
            Career Opportunity
          </Link>

          {/* Contact Us */}
          <Link
            to="/contact"
            className="flex items-center gap-2 text-gray-700 hover:text-sky-600"
            onClick={() => setMenuOpen(false)}
          >
            <FaPhoneAlt />
            Contact Us
          </Link>

          {/* Call Now (Stylish Number) */}
          <a
            href="tel:+918295000455"
            className="flex items-center gap-2 text-sky-600 font-bold hover:text-blue-700 transition"
            onClick={() => setMenuOpen(false)}
          >
            | +91 8295000455
          </a>

        <button
  className="bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-full font-semibold transition-all flex items-center gap-2 hover:shadow-lg"
  style={{ backgroundColor: COLORS.secondary }}
  onClick={() => setShowModal(true)}
>
<FaRocket
  style={{ color: "#0EA5E9" }}
  className="transition-transform duration-300 hover:-translate-y-1 hover:scale-110"
/>


  Get Started Free →
</button>

        </div>
      </div>

      <ERPModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </nav>
  );
};

export default Navbar;
